/**
 * P-B 长图 — 选中区块属性 inspector(按家族分派;设计稿 13 类 inspector 收敛为家族表单)。
 *
 * 真数据红线:数据块所有数值/文案在此手填(粘贴多行 / P-B.1 起表格块可直传 Excel,
 * xlsx 懒加载独立 chunk 仅选文件时下载),渲染层原样呈现,AI 不编数字。
 */
import { maskVendor } from "@/shared/lib/vendorMask";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Sparkles, Trash2, Upload } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import { toast } from "@/shared/hooks/use-toast";
import { posterErrorToast } from "@/shared/lib/poster-errors";
import { cn } from "@/shared/lib/utils";
import { posterService } from "../../api/posterService";
import { SKU, imageVariantKey, pricingService } from "@/entities/pricing/api/pricingService";
import type { CanvasBlockForm } from "../model/canvasBridge";
import { STYLE_META, refitTableRows } from "../model/blocks";
import {
  TEXT_PAIR_FALLBACK_TAG,
  formatSeriesValue,
  parseSeriesValue,
} from "../model/numericSeries";
import { GEN_MODE_QUESTION, genModeChoices } from "../model/resultLanguage";
import { EasyChoiceRow } from "./EasyChoiceRow";
import { RefinableField } from "./RefinableField";
import { SlotFields } from "./SlotFields";
import { slotDigestLine, slotSentence } from "../model/slotWords";
import type { ImageSlots } from "../model/canvas";
import { IMAGE_ACCEPT } from "@/shared/lib/uploadImageFormats";
import { errorToast } from "@/shared/lib/apiErrors";

interface Props {
  block: CanvasBlockForm;
  /**
   * 写回 props。同步编辑不带第二参;**异步结果**(参考图上传)带 `blockId` = 发起时的那一块 ——
   * 0917 测试机真单:上传期间用户切到了正文块,结果落到了完成那一刻选中的块。上层按 id 写回。
   */
  onPropsChange: (patch: Record<string, unknown>, blockId?: string) => void;
  onRename: (name: string) => void;
  /**
   * V2 小白态:**只露内容字段**(设计方案 §3.6 最后一行)。
   *
   * 藏的是「参数」不是「能力」——区块名称 / 本块比例 / 对齐 / 背景 / 行式或卡片,
   * 这些都是同一份内容的呈现旋钮,小白态不问、精编态原样都在。
   * 留下的一律是**内容**:文案、条目、数值、画面描述、参考图。
   * ★「生成方式」不藏,换成结果语言的问句(它决定成品像不像实物,是内容决策不是参数)。
   */
  /**
   * 数字白名单的锚(用户原文 + 图上 OCR 原文 + 产品库原文)。
   * ★传了才出「AI 优化」按钮 —— 没有锚的改写等于让模型随便编数字。
   */
  rawBlob?: string;
  /** 常驻内容区额外要挂的东西(智能稿的 用哪张照片 / 保真 / 数字要不要零误差)。 */
  contentExtras?: React.ReactNode;
  /** 版面参数折叠区里额外要挂的东西(智能稿的 分量 / 排法 / 怎么摆)。 */
  extras?: React.ReactNode;
  /**
   * 把当前块换成另一种样式(7.29 反馈任务 8 的「一键切块」)。
   *
   * 只在数值填了文字时用得上:用户想做的是「我家 vs 别人家」的**文字**对照,而那种块本来
   * 就有(前后对比)。不传则按钮禁用并给出手动办法 —— 少一个能力好过点了没反应。
   */
  onSwitchStyle?: (styleTag: string) => void;
  /** 画布用途 —— 「用产品实拍图」这类词面按它换(corp=公司图,2026-08-03 开卡批)。 */
  useCase?: string | null;
  /**
   * 参考图/示例图上传口(P2)。默认租户 `/poster/uploads`(存 admin 自己租户桶,短效);
   * admin 建模板时**注入平台版**(`posterTemplateService.uploadBlockImage`)→ 落平台桶、长期可跨租户访问,
   * 才能让租户套用后右画布看到那张示例图。返回 objectKey 落 `props.refKey`。
   */
  uploadRefImage?: (file: File) => Promise<{ objectKey: string }>;
  /** LI30-B 原图直出块:选/换图回调(objectKey 数组 + 本地预览 URL + 发起时的块 id)。给了才可交互。 */
  onOriginalKeysChange?: (keys: string[], previewUrl?: string, blockId?: string) => void;
}

const Seg = ({
  options,
  value,
  onChange,
}: {
  options: Array<{ v: string; label: string }>;
  value: string;
  onChange: (v: string) => void;
}) => (
  <div className="inline-flex flex-wrap gap-1 rounded-full border border-border/60 bg-card/40 p-0.5">
    {options.map((o) => (
      <button
        key={o.v}
        type="button"
        onClick={() => onChange(o.v)}
        aria-pressed={value === o.v}
        className={cn(
          "rounded-full px-3 py-1 text-xs font-medium transition-colors",
          value === o.v
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:text-foreground"
        )}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-1.5 text-2xs font-medium text-muted-foreground">{children}</div>
);

/**
 * pairs 族各块的占位符(0810 琳琳:「数据来源这块写来源就行了吧,为啥还要加名称-内容?」)。
 *
 * ★她问的其实是**「这两个格子分别该写什么」**,而不是「能不能只写一个」——
 *   数据来源本来就成对(「销量数据 → 天猫后台 2025-07」),成对是对的;
 *   坏在这九种块**共用一句「名称 / 内容」**,谁看都不知道自己该往哪填。
 * ★所以不动数据结构(渲染器按 label/value 画,label 空掉会留一条 96u 的空白轨),
 *   只把占位符换成每种块自己的话 —— 让人一眼看出为什么要两格。
 */
const PAIRS_PLACEHOLDER: Record<string, [string, string]> = {
  spec: ["参数名(如 材质)", "参数值(如 A级实木)"],
  infocard: ["名目", "内容"],
  price: ["套餐名", "价格"],
  rank: ["名次 / 品名", "上榜理由或数据"],
  timeline: ["2024", "事件"],
  flow: ["第几步", "做什么"],
  sales: ["指标名", "数值(如 月销 1.2 万)"],
  honor: ["奖项 / 资质名", "颁发方与年份"],
  media: ["媒体名", "报道标题或结论"],
};

/**
 * 同一个 `infocard` 在骨架里叫两种名字:榜单用途下它是「数据来源」,别处就是通用「信息卡」。
 *
 * ★所以**不能拿一种用法把占位符收窄** —— 琳琳问的是数据来源那一种,
 *   但真按「出处」写死,通用信息卡的人就会被一句不相干的话指错方向。按块名分流。
 */
function isSourceCard(styleTag: string, name: string): boolean {
  return styleTag === "infocard" && name.includes("来源");
}

/** 只有确实容易填错的块才给一句;别每块都挂提示,那会变成噪音。 */
const PAIRS_HINT: Record<string, string> = {
  infocard: "左边写这条数据是什么、右边写它从哪来 —— 两格合起来才是一条可核对的来源。",
};

/** 分块题头(默认那句「条目 · 真数据手填」对来源块不贴切)。 */
const PAIRS_FIELD_LABEL: Record<string, string> = {
  infocard: "来源逐条 · 真出处手填,AI 不编",
};

/** 数据来源那一种用法自己的占位符。 */
const SOURCE_PLACEHOLDER: [string, string] = ["哪一项数据", "出处(如 天猫后台 2025-07)"];

/**
 * {label, value} 行编辑器(spec/infocard/price/rank/timeline/flow/sales/honor/media 共用)。
 *
 * ★`numericErrors`(2026-07-30,7.29 反馈任务 8):图表族(comparebar/line/pie)的 value 必须是
 * 数字,以前非数字被 `Number(v) || 0` **静默吞成 0** —— 用户以为自己填了东西。现在由调用方
 * 把逐行的报错传进来,渲在那一行下面;**用户敲的字保持原样不被改写**,他能看见自己填了什么。
 */
/** 实拍分格卡的逐格文案(与图一一对应;标题必填、说明可空)。 */
function CardsEditor({
  cards,
  photoCount,
  onChange,
}: {
  cards: Array<{ title: string; desc: string }>;
  photoCount: number;
  onChange: (cards: Array<{ title: string; desc: string }>) => void;
}) {
  // ★按**图数**列格子,不按 cards 数:渲染层就是按图数走的(图比卡少时多出来的卡不会画),
  //   编辑器要和成品长一个样,否则用户填了第 4 格却永远看不到它。
  const rows = Array.from(
    { length: Math.max(photoCount, 1) },
    (_, i) => cards[i] ?? { title: "", desc: "" }
  );
  const patch = (i: number, key: "title" | "desc", v: string) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="space-y-1 rounded-md border border-border/60 bg-card/40 p-2">
          <div className="text-2xs text-muted-foreground">第 {i + 1} 张图</div>
          <Input
            value={r.title}
            maxLength={24}
            placeholder="这张图讲什么(如:4D 扶手)"
            onChange={(e) => patch(i, "title", e.target.value)}
            className="h-8 text-xs"
          />
          <Input
            value={r.desc}
            maxLength={80}
            placeholder="一句说明(可空;只写你有依据的话)"
            onChange={(e) => patch(i, "desc", e.target.value)}
            className="h-8 text-xs"
          />
        </div>
      ))}
      {photoCount === 0 && (
        <p className="text-2xs text-muted-foreground">
          这一块还没有照片 —— 回上一步把细节图传上来,或把它删掉。
        </p>
      )}
    </div>
  );
}

/** 一块里最多几行 —— 750 宽版面的硬事实(再多每行只剩十几像素高,糊成一坨)。
 *  ★提成常量是为了「按钮禁用条件」与「到顶那句解释」永远说同一个数(两处写死必漂)。 */
const ITEMS_MAX = 16;

/**
 * 列表项文本框(LI30,0910 老板实反「无法回车换行」)。
 * ★老写法是受控直连:value=items.join("\n"),onChange 里 split→trim→filter(Boolean)
 *   再写回 —— 刚敲出的空行当拍被滤掉回流,换行就地消失,看起来像"禁止回车"。
 *   不是设计意图(字段标注本来就是"一行一条")。
 * 修:textarea 持本地草稿,空行/首尾空白在草稿里合法存在;落 props 时才规整
 *   (trim/filter/12 条帽)。块切换靠调用处 key={block.id} 重挂,外部改动(AI 优化/
 *   重排回填)靠 joined 与上次落值不同才覆盖草稿 —— 自己打字的回流永不覆盖。
 */
function ListItemsEditor({
  items,
  onChange,
}: {
  items: string[];
  onChange: (items: string[]) => void;
}) {
  const [draft, setDraft] = useState(() => items.join("\n"));
  const lastPushedRef = useRef(items.join("\n"));
  useEffect(() => {
    const joined = items.join("\n");
    if (joined !== lastPushedRef.current) {
      setDraft(joined);
      lastPushedRef.current = joined;
    }
  }, [items]);
  return (
    <Textarea
      data-testid="list-items-editor"
      value={draft}
      rows={4}
      onChange={(e) => {
        setDraft(e.target.value);
        const parsed = e.target.value
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean)
          .slice(0, 12);
        lastPushedRef.current = parsed.join("\n");
        onChange(parsed);
      }}
      className="text-xs"
    />
  );
}

function PairsEditor({
  items,
  onChange,
  labelPh,
  valuePh,
  numericErrors,
}: {
  items: Array<{ label: string; value: string }>;
  onChange: (items: Array<{ label: string; value: string }>) => void;
  labelPh: string;
  valuePh: string;
  numericErrors?: Record<number, string>;
}) {
  return (
    <div className="space-y-1.5">
      {items.map((it, i) => (
        <div key={i} className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Input
              value={it.label}
              maxLength={40}
              placeholder={labelPh}
              onChange={(e) =>
                onChange(items.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))
              }
              className="h-8 w-28 text-xs"
            />
            <Input
              value={it.value}
              maxLength={120}
              placeholder={valuePh}
              aria-invalid={numericErrors?.[i] ? true : undefined}
              onChange={(e) =>
                onChange(items.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))
              }
              className={cn(
                "h-8 flex-1 text-xs",
                numericErrors?.[i] && "border-destructive focus-visible:ring-destructive"
              )}
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 text-muted-foreground"
              disabled={items.length <= 1}
              aria-label="删除此行"
              onClick={() => onChange(items.filter((_, j) => j !== i))}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
          {numericErrors?.[i] && (
            <div role="alert" className="pl-[7.25rem] text-2xs text-destructive">
              {numericErrors[i]}
            </div>
          )}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 gap-1 text-xs"
          disabled={items.length >= ITEMS_MAX}
          onClick={() => onChange([...items, { label: "", value: "" }])}
        >
          <Plus className="h-3 w-3" />
          加一行
        </Button>
        {/**
         * ★到顶了要说**为什么**、并给出路(爱民 0812:「只能添加 16 行,不要限制」)。
         *
         * 限制本身是真的:一块里塞 16 行,750 宽的版面上每行只剩十几个像素高,
         * 出来就是糊成一坨 —— 这不是我们小气,是画幅的硬事实。
         * 但原来只是把按钮 `disabled` 掉,**一个字都不解释**:用户点不动、又不知道为啥,
         * 于是读成「产品在拦我」。他真正要的不是「解除上限」,是**把剩下的内容继续列完** ——
         * 所以这句话必须带出路:再加一块同类型的块接着列。
         */}
        {items.length >= ITEMS_MAX && (
          <span data-testid="items-cap-hint" className="text-2xs text-muted-foreground">
            一块最多 {ITEMS_MAX} 行 —— 再多这一块会被压得看不清。还有要列的,
            <b>再加一块同类型的块接着列</b>。
          </span>
        )}
      </div>
    </div>
  );
}

/** 卖点上图:出图引擎能画的上限(与 ai `ImageProps.overlayLines` 同源)。 */
const OVERLAY_MAX_LINES = 3;
const OVERLAY_MAX_CHARS = 14;

/** 原文 → 真正会画进画面的那几行(丢行不丢块,与 ai 侧同口径)。 */
function normalizeOverlayLines(raw: string): string[] {
  return raw
    .split("\n")
    .map((s) => s.trim().slice(0, OVERLAY_MAX_CHARS))
    .filter(Boolean)
    .slice(0, OVERLAY_MAX_LINES);
}

/**
 * 卖点上图编辑器 —— 让人**先敲得出来**,再告诉他哪儿超了(爱民 0810:
 * 「无法回车,一行是如何得来?限制字太麻烦」)。
 *
 * ★老写法在 `onChange` 里当场
 *     .map(s => s.slice(0, 14)).filter(s => s.trim()).slice(0, 3)
 *   然后把结果塞回受控 value。三个副作用叠在一起,全都发生在**用户正在打字的那一刻**:
 *     · `filter(s => s.trim())` 让**空行根本存不住** → 按下回车什么也没发生 = 「无法回车」;
 *     · `slice(0,14)` 让第 15 个字打不出来 —— 不是提示,是字直接消失;
 *     · `slice(0,3)` 让第 4 行刚敲就没。
 *   用户看到的是「我敲的东西凭空不见了」,自然说「限制字太麻烦」。
 *
 * ★限制本身要留(引擎真的只画得下这么多),改的是**什么时候执行**:
 *   输入框收原文(想怎么敲怎么敲、回车照常),下面**实时**标出哪几行超、超了会怎样;
 *   落到 props 的仍是规范化后的结果 —— 出图侧拿到的东西一个字没变。
 */
function OverlayLinesEditor({
  value,
  onChange,
}: {
  value: string[];
  onChange: (lines: string[] | undefined) => void;
}) {
  // 原文只活在这一层:props 里存的是规范化结果,回不出用户敲的空行/超长行。
  const [raw, setRaw] = useState(() => value.join("\n"));
  const lines = raw.split("\n");
  const overLong = lines
    .map((l, i) => ({ i, len: l.trim().length }))
    .filter((x) => x.len > OVERLAY_MAX_CHARS);
  const kept = normalizeOverlayLines(raw);
  const dropped = lines.filter((l) => l.trim()).length - kept.length;

  return (
    <div>
      <FieldLabel>卖点上图 · 叠在画面上的短句(一行一条,最多 {OVERLAY_MAX_LINES} 行)</FieldLabel>
      <Textarea
        aria-label="卖点上图"
        rows={3}
        placeholder={"例:一件顶三件\n通勤宴请都能穿"}
        value={raw}
        onChange={(e) => {
          setRaw(e.target.value);
          const next = normalizeOverlayLines(e.target.value);
          onChange(next.length ? next : undefined);
        }}
        className="text-xs"
      />
      <p className="mt-1 text-2xs text-muted-foreground">
        这几句由出图引擎画进画面里(不是系统排版),每条最多 {OVERLAY_MAX_CHARS} 字。
      </p>
      {(overLong.length > 0 || dropped > 0) && (
        <p data-testid="overlay-lines-warn" className="mt-1 text-2xs text-warning-foreground">
          {overLong.length > 0 && (
            <>
              第 {overLong.map((x) => x.i + 1).join("、")} 行超了(
              {overLong.map((x) => `${x.len} 字`).join("、")}),画进画面时只取前 {OVERLAY_MAX_CHARS}{" "}
              字。
            </>
          )}
          {dropped > 0 && (
            <>
              {overLong.length > 0 ? " " : ""}
              只有前 {OVERLAY_MAX_LINES} 行会画上去,多出的 {dropped} 行不画。
            </>
          )}
        </p>
      )}
    </div>
  );
}

export function BlockInspector({
  block,
  onPropsChange,
  onRename,
  contentExtras,
  extras,
  rawBlob,
  onSwitchStyle,
  useCase = null,
  uploadRefImage = posterService.uploadRefImage,
  onOriginalKeysChange,
}: Props) {
  const xlsxInputRef = useRef<HTMLInputElement | null>(null);
  const meta = STYLE_META[block.styleTag];
  const p = block.props as Record<string, unknown>;

  // ★7.29 反馈任务 8:图表族数值的**用户原文**。props 里只存合法数字,但输入框要显示用户
  // 真正敲进去的字 —— 否则「填文字 → 被改写成 0」的观感一模一样,只是换了个数字。
  // 换块(id 变)时清空,不要把上一块的半截输入带过来。
  const [rawSeries, setRawSeries] = useState<string[] | null>(null);
  const rawSeriesBlockId = useRef(block.id);
  if (rawSeriesBlockId.current !== block.id) {
    rawSeriesBlockId.current = block.id;
    if (rawSeries !== null) setRawSeries(null);
  }
  const seriesProp = Array.isArray(p.series)
    ? (p.series as Array<{ label: string; value: number | null }>)
    : [];
  const seriesRows = seriesProp.map((s, i) => ({
    label: s.label,
    value: rawSeries?.[i] ?? formatSeriesValue(s.value),
  }));
  const seriesErrors: Record<number, string> = {};
  seriesRows.forEach((row, i) => {
    const parsed = parseSeriesValue(row.value);
    if (!parsed.ok && parsed.error) seriesErrors[i] = parsed.error;
  });

  /**
   * 一段话 → 补齐空槽(D3 第二腿)。只在**有槽**的块上给按钮:老稿走自由文本路,
   * 那条路上没有槽可补(硬补等于给老稿凭空造结构)。
   *
   * ★回写用 ai 返回的整份 slots:它保证了「已填的不覆盖」,前端再合并一次只会两处判据打架。
   * ★degraded 不弹红:用户点的是「补细节」,补不出来说一句实话就行(reason 走 toast 的
   *   default 档,不是 destructive)。
   */
  const slotsForInfer = p.slots as ImageSlots | undefined;
  /** 还有没有空格子可补 —— 没有就别让用户白点一次(也白花一次 LLM 调用)。 */
  const hasEmptySlot = Boolean(
    slotsForInfer &&
      (!String(slotsForInfer.environment ?? "").trim() ||
        !String(slotsForInfer.extra ?? "").trim() ||
        !slotsForInfer.shotType ||
        !slotsForInfer.angle ||
        !slotsForInfer.lighting ||
        !slotsForInfer.material ||
        !slotsForInfer.tone ||
        !slotsForInfer.negativeSpace)
  );
  const inferSlotsMut = useMutation({
    mutationFn: () =>
      posterService.inferLongImageSlots({
        sentence: String(slotsForInfer?.subject ?? ""),
        slots: (slotsForInfer ?? {}) as Record<string, unknown>,
        ...(rawBlob ? { rawBlob } : {}),
        blockLabel: block.name,
      }),
    onSuccess: (res) => {
      if (res.degraded) {
        toast({ title: "没补上", description: maskVendor(res.reason) || "这句话补不出更多细节" });
        return;
      }
      const next = res.slots as unknown as ImageSlots;
      onPropsChange({ slots: next, prompt: slotSentence(next).join(";") });
      toast({
        title: `AI 补了 ${res.filled.length} 处画面细节`,
        description: slotDigestLine(next) || undefined,
      });
    },
    onError: (e: unknown) => toast(posterErrorToast(e)),
  });

  /**
   * ★异步结果的归属块 = **发起时**的这一块(0917 测试机真单 5872549102593 等 7 单):
   *   本组件不按块 id 重挂,切块只是换 `block` prop;useMutation 的 options 每次渲染都同步到
   *   mutation 上 → 完成时调的是**最新**的回调,曾把参考图写进完成那一刻选中的「正文」块
   *   (ai 侧 TextProps extra=forbid 整单 422)。三条异步路都把发起时的块 id 随 variables 带回。
   */
  type BlockTarget = { id: string; name: string };
  const targetOf = (): BlockTarget => ({ id: block.id, name: block.name });
  const noteLandedElsewhere = (target: BlockTarget, what: string) => {
    if (target.id !== block.id) {
      toast({
        title: `${what}已放进「${target.name}」`,
        description: "上传期间你切到了别的块 —— 结果还是给发起时那一块。",
      });
    }
  };

  const uploadOriginalMut = useMutation({
    // ★宽度纪律(0910 老板:「唯一要注意的是宽度,保证拼得起来」):直出图按整图宽等比
    //   拼入,太窄=放大糊,极端宽高比=一屏被撑爆或压成条。前置读原图尺寸把关:
    //   宽 <600 硬拒;<1500 放行但提醒(2K 成品会放大);宽高比出 [1:4, 3:1] 硬拒。
    mutationFn: async ({ file }: { file: File; target: BlockTarget }) => {
      const dims = await new Promise<{ w: number; h: number }>((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new window.Image();
        img.onload = () => {
          resolve({ w: img.naturalWidth, h: img.naturalHeight });
          URL.revokeObjectURL(url);
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("这张图读不出来,换一张试试"));
        };
        img.src = url;
      });
      if (dims.w < 600) {
        throw new Error(`这张图宽只有 ${dims.w}px,拼进成品会明显发糊 —— 请用宽 ≥1500px 的成图`);
      }
      if (dims.h > dims.w * 4) {
        throw new Error("这张图太长了(高超过宽的 4 倍)—— 请拆成几张再分块拼入");
      }
      if (dims.w > dims.h * 3) {
        throw new Error("这张图太扁了(宽超过高的 3 倍)—— 横幅类素材建议走横宽长图");
      }
      if (dims.w < 1500) {
        toast({
          title: "可以用,但清晰度一般",
          description: `这张图宽 ${dims.w}px,2K 成品会放大到 1500px —— 有更高清的版本更好。`,
        });
      }
      return uploadRefImage(file);
    },
    // 落 objectKey(稳定寻址,ai 侧 originals 现签下载);tempUrl 只作本地预览不进 wire。
    onSuccess: (res: { objectKey: string; tempUrl?: string }, { target }) => {
      onOriginalKeysChange?.([res.objectKey], res.tempUrl, target.id);
      noteLandedElsewhere(target, "成图");
    },
    onError: (e: unknown) => toast(posterErrorToast(e)),
  });

  // ── 屏卡 P2(0910 老板拍板):「用 AI 画一张当原图」────────────────────────────
  // 走海报单图管线(quote→createTask→轮询),产物以 asset:{id} 引用 adopt 进原图槽
  // (backend 派发时解析成 objectKey,ai 原件屏现签 MEDIA 桶,渲染链零改动)。
  // 掉字治理的分工:文字准确性归单图链(不满意可再画一张),长图 AI 只管风格与衔接。
  const [aiDrawOpen, setAiDrawOpen] = useState(false);
  const [aiDrawPrompt, setAiDrawPrompt] = useState("");
  // 0911 老板:比例加全 —— 与屏画幅/商品主图同一套五档(海报 createTask 全支持)
  const [aiDrawRatio, setAiDrawRatio] = useState<"3:4" | "4:5" | "1:1" | "9:16" | "16:9">("3:4");
  const aiDrawMut = useMutation({
    mutationFn: async (_target: BlockTarget) => {
      const prompt = aiDrawPrompt.trim();
      if (!prompt) throw new Error("先写一句要画什么(会按整块直出,文字请写进描述里)");
      // 批 L 报价单纪律:落正式报价单再建任务,确认数=扣费数单一来源在 backend
      const q = await pricingService.quote({
        skuCode: SKU.posterImage,
        variantKey: imageVariantKey("OPENAI", "1K"),
        qty: 1,
        dryRun: false,
      });
      const { taskId } = await posterService.createTask({
        quoteId: q.quoteId ?? undefined,
        name: `AI 原图 · ${prompt.slice(0, 20)}`,
        renderMode: "AI_RENDER",
        provider: "OPENAI",
        ratio: aiDrawRatio,
        resolutionTier: "1K",
        requestedCount: 1,
        content: {
          sourceType: "FREEFORM_RAW",
          productName: "",
          sellingPoints: [],
          rawPrompt: prompt,
        },
        brand: { qrCodeMode: "NONE" },
      });
      // 单图 1K 一般 <1 分钟;上限 4 分钟,超时按失败报(用户可去作品库找)。
      // 先查后睡:快单少等一拍,测试也不用假时钟。
      for (let i = 0; i < 48; i++) {
        const t = await posterService.getTask(taskId);
        if (t.status === "SUCCESS") {
          const assets = await posterService.listAssets(taskId);
          const a = assets.find((x) => x.status === "SUCCESS");
          if (!a) throw new Error("出图成功但没拿到产物,去作品库看看这单");
          return a;
        }
        if (t.status === "FAILED") {
          throw new Error(maskVendor(t.errorMessage) || "这张没画成(未画成不扣费),换个说法再试");
        }
        await new Promise((r) => setTimeout(r, 5000));
      }
      throw new Error("画图超时了 —— 任务还在后台跑,稍后到作品库找这张「AI 原图」");
    },
    onSuccess: (a, target) => {
      onOriginalKeysChange?.([`asset:${a.id}`], a.baseUrl ?? undefined, target.id);
      setAiDrawOpen(false);
      setAiDrawPrompt("");
      toast({
        title: `画好了,已放进「${target.name}」`,
        description: "不满意可再画一张替换,或改用上传。",
      });
    },
    onError: (e: unknown) => toast(posterErrorToast(e)),
  });

  const uploadRefMut = useMutation({
    mutationFn: ({ file }: { file: File; target: BlockTarget }) => uploadRefImage(file),
    // codex 对抗修:落库存稳定 objectKey(refKey),backend 派发瞬间兑换 presigned refUrl
    // —— tempUrl 直存会在隔天草稿/retry 时过期(与自由创作 coverImageRef 同口径)。
    onSuccess: (res, { target }) => {
      onPropsChange({ refKey: res.objectKey, genMode: "i2i" }, target.id);
      noteLandedElsewhere(target, "参考图");
    },
    onError: (e: unknown) => toast(posterErrorToast(e)),
  });

  const family = (() => {
    // ★实拍分格卡自成一族(2026-08-05):它没有「画面描述 / 参考图 / 窄槽」那一套 ——
    //   画面就是用户的实拍,要编的是每一格的标题与说明。
    //   套用 image 那套面板会让用户对着一堆管不着这块的旋钮。
    if (block.styleTag === "photocards") return "photocards";
    // ★原图直出(LI30-B):canvas.originalKeys 槽在场 = 直出模式 —— 它没有画面描述/
    //   参考图/窄槽那一套(画面就是用户的成图),套 image 面板全是管不着的旋钮。
    if (meta.primitive === "image" && Array.isArray(block.canvas?.originalKeys)) {
      return "original";
    }
    if (meta.primitive === "image") return "image";
    if (block.styleTag === "numlist" || block.styleTag === "twocol") return "list";
    if (["comparebar", "line", "pie"].includes(block.styleTag)) return "chart";
    if (["stat", "percent", "beforeafter"].includes(block.styleTag)) return "stat";
    if (block.styleTag === "table") return "table";
    if (block.styleTag === "rating") return "rating";
    if (block.styleTag === "qr") return "qr";
    if (meta.primitive === "data") return "pairs";
    return "text";
  })();

  /**
   * ★「内容常驻 · 参数折叠」—— **唯一**的分层判据,取自本文件头自己早就写对的那一条:
   *   「藏的是**参数**不是**能力**……留下的一律是**内容**」。
   *
   * 改之前这条判据被 `easy` 开关又横切了一刀,切出了**第二套词表**:同一个 state
   * 在简单态叫「这块要放什么画面」、精编态叫「画面描述」,连控件类型都换
   * (缩略图 ↔ 下拉框),一共六对。用户换个模式看到的不是"多几个旋钮",是"东西全变了"。
   * 现在只有一套词表一套控件,想要更多的人展开折叠区 ——
   * 而不是换一个模式换一套说法。
   */
  const paramFields = (
    <>
      {extras}
      <div>
        <FieldLabel>区块名称</FieldLabel>
        <Input
          value={block.name}
          maxLength={40}
          onChange={(e) => onRename(e.target.value)}
          className="h-8 text-xs"
        />
      </div>
      {family === "image" && (
        <div>
          <FieldLabel>本块比例</FieldLabel>
          <Seg
            options={[
              // LI30(0910 拍板⑤):3 档 → 5 档,词面对齐商品主图(数字+中文名+渠道)。
              { v: "3:4", label: "3:4 竖图" },
              { v: "4:5", label: "4:5 近方" },
              { v: "1:1", label: "1:1 方图" },
              { v: "9:16", label: "9:16 长图" },
              { v: "16:9", label: "16:9 横版" },
            ]}
            value={String(p.blockRatio ?? "3:4")}
            onChange={(v) => onPropsChange({ blockRatio: v })}
          />
        </div>
      )}
      {family === "text" && (
        <div className="flex flex-wrap gap-3">
          <div>
            <FieldLabel>对齐</FieldLabel>
            <Seg
              options={[
                { v: "center", label: "居中" },
                { v: "left", label: "居左" },
              ]}
              value={String(p.align ?? "center")}
              onChange={(v) => onPropsChange({ align: v })}
            />
          </div>
          <div>
            <FieldLabel>背景</FieldLabel>
            <Seg
              options={[
                { v: "plain", label: "透明" },
                { v: "tint", label: "浅底" },
                { v: "accent", label: "主色" },
              ]}
              value={String(p.bg ?? "plain")}
              onChange={(v) => onPropsChange({ bg: v })}
            />
          </div>
        </div>
      )}
      {family === "pairs" && ["spec", "infocard", "honor", "media"].includes(block.styleTag) && (
        <div>
          <FieldLabel>布局</FieldLabel>
          <Seg
            options={[
              { v: "rows", label: "行式" },
              { v: "cards", label: "2 列卡片" },
            ]}
            value={String(p.layout ?? "rows")}
            onChange={(v) => onPropsChange({ layout: v })}
          />
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-3.5">
      {contentExtras}
      {family === "photocards" && (
        <>
          <div>
            <FieldLabel>这一段的小标题</FieldLabel>
            <Input
              value={String(p.heading ?? "")}
              maxLength={24}
              placeholder="如:细节实拍"
              onChange={(e) => onPropsChange({ heading: e.target.value })}
              className="h-8 text-xs"
            />
          </div>
          <div>
            <FieldLabel>每张图配的字</FieldLabel>
            <CardsEditor
              cards={
                Array.isArray(p.cards)
                  ? (p.cards as Array<{ title?: string; desc?: string }>).map((c) => ({
                      title: String(c?.title ?? ""),
                      desc: String(c?.desc ?? ""),
                    }))
                  : []
              }
              photoCount={(p.refKey ? 1 : 0) + (Array.isArray(p.refKeys) ? p.refKeys.length : 0)}
              onChange={(cards) => onPropsChange({ cards })}
            />
            <p className="mt-1 text-2xs text-muted-foreground">
              这些字由系统精确排版,不进出图引擎 —— 不会被画糊,也不会被改写。
            </p>
          </div>
        </>
      )}
      {family === "original" && (
        <div className="space-y-2">
          <FieldLabel>原图直出 · 原样拼进成品,AI 不重画</FieldLabel>
          {block.canvas?.originalPreviewUrl && (
            <img
              src={block.canvas.originalPreviewUrl}
              alt="已选的成图"
              className="max-h-56 w-full rounded-md border border-border/60 object-contain"
            />
          )}
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border/60 bg-card/40 px-3 py-2 text-xs">
            {uploadOriginalMut.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            {block.canvas?.originalKeys?.length ? "已选图,点击换一张" : "上传我的成图"}
            <input
              type="file"
              accept={IMAGE_ACCEPT}
              className="hidden"
              data-testid="original-upload-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadOriginalMut.mutate({ file: f, target: targetOf() });
                e.target.value = "";
              }}
            />
          </label>
          <button
            type="button"
            data-testid="original-ai-draw-toggle"
            className="flex w-full items-center gap-2 rounded-md border border-border/60 bg-card/40 px-3 py-2 text-xs"
            onClick={() => setAiDrawOpen((v) => !v)}
          >
            <Sparkles className="h-3.5 w-3.5" />
            没有成图?用 AI 画一张(与自由创作同价)
          </button>
          {aiDrawOpen && (
            <div className="space-y-2 rounded-md border border-border/60 bg-card/40 p-2">
              <Textarea
                value={aiDrawPrompt}
                onChange={(e) => setAiDrawPrompt(e.target.value)}
                placeholder="要画什么?画面、文字都写清楚,例:深蓝科技感横幅,大字标题「三步接入」,配三个白色图标"
                rows={3}
                maxLength={2000}
                className="text-[13px]"
                data-testid="original-ai-draw-prompt"
              />
              <div className="flex items-center gap-1.5">
                {(["3:4", "4:5", "1:1", "9:16", "16:9"] as const).map((r) => (
                  <button
                    key={r}
                    type="button"
                    aria-pressed={aiDrawRatio === r}
                    className={cn(
                      "rounded-md border px-2 py-1 text-2xs",
                      aiDrawRatio === r
                        ? "border-primary font-medium text-primary"
                        : "border-border/60 text-muted-foreground"
                    )}
                    onClick={() => setAiDrawRatio(r)}
                  >
                    {r}
                  </button>
                ))}
                <Button
                  type="button"
                  size="sm"
                  className="ml-auto h-7 text-xs"
                  disabled={aiDrawMut.isPending || !aiDrawPrompt.trim()}
                  data-testid="original-ai-draw-submit"
                  onClick={() => aiDrawMut.mutate(targetOf())}
                >
                  {aiDrawMut.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                  {aiDrawMut.isPending ? "画图中(约 1 分钟)…" : "画这一张"}
                </Button>
              </div>
              <p className="text-2xs leading-relaxed text-muted-foreground">
                按海报单图 1K 计费(报价即实扣);画好自动放进这一块,原样拼进成品。
                文字要不要、写什么,以你描述为准 —— 不满意可再画一张替换。
              </p>
            </div>
          )}
          <p className="text-2xs leading-relaxed text-muted-foreground">
            这块独立成一屏,按整图宽度等比原样拼入(一个像素不重画)——适合放你在
            自由创作里出的高质量成图或现成设计稿。清晰起见建议宽 ≥1500px。
            <span className="font-medium text-foreground">上传/AI 画好后拼接本身不另扣积分。</span>
          </p>
        </div>
      )}
      {family === "image" && (
        <>
          {/* ★1015 P4:图片块只出图 —— 全用图片块出过一张 84 分的无字长图。字要放文字/数据块,
              或填本块下面「画面上要写的字」(那栏会被画进图)。 */}
          <p className="text-2xs text-muted-foreground" data-testid="image-block-no-text-note">
            此块只出图,不印文字 —— 要在图上出字,用本块下方的文字栏;正文、卖点请放文字/数据块。
          </p>
          <EasyChoiceRow
            question={GEN_MODE_QUESTION}
            choices={genModeChoices(useCase)}
            value={String(p.genMode) === "i2i" ? "i2i" : "t2i"}
            onPick={(v) => onPropsChange({ genMode: v })}
          />
          {String(p.genMode) === "i2i" && (
            <div>
              <FieldLabel>这块要参考的产品照片</FieldLabel>
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border/60 bg-card/40 px-3 py-2 text-xs">
                {uploadRefMut.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
                {p.refKey || p.refUrl ? "已上传,点击换图" : "上传参考图"}
                <input
                  type="file"
                  accept={IMAGE_ACCEPT}
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadRefMut.mutate({ file: f, target: targetOf() });
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
          )}
          {/* ★画面描述:有槽走五格,没槽走老的自由文本框(2026-08-02,条 3)。
              ★**存量草稿不迁移** —— 老稿没有 slots,照样是那个文本框,一个字不用改就能出图。
                迁移脚本是另一种「静默改掉用户的稿子」,这一批不做。 */}
          {p.slots ? (
            /* ★一段话在前,六格收进折叠(2026-08-05,D3)。
               用户反馈原话:「给用户的感觉就是只有一段文案就行,不要做那么多」——
               主体/取景/环境/光影/留白/还要有什么六格摊开,是把**我们的实现结构**摊给用户填。
               ★主控绑的是 `slots.subject` 而不是 `prompt`:ai 侧编译真正消费的是 slots
               (canvasBridge 那条注释写着),把主控绑到 prompt 会让用户改了却不生效 ——
               一条静默死通道,比多几个框糟得多。
               ★其余槽空着也不拦:该由规划补齐,补不上就按写的这段话出图。 */
            <>
              <RefinableField
                label="这块要放什么画面"
                value={String((p.slots as ImageSlots).subject ?? "")}
                onChange={(v) => {
                  const next = { ...(p.slots as ImageSlots), subject: v };
                  onPropsChange({ slots: next, prompt: slotSentence(next).join(";") });
                }}
                rawBlob={rawBlob}
                blockLabel={block.name}
                multiline
                /* ★120 而不是 300(2026-08-06 线上 422 修):这个框绑的是 `slots.subject`,
                   ai 侧 `ImageSlots.subject` 是 `max_length=120`。D3 把主控从 `prompt`(1200)
                   改绑到 `slots.subject`(120)时,**长度约束只改了一边** —— 300 的框喂 120 的字段,
                   用户写到第 121 个字才在点「确认出图」时吃到
                   `POSTER_INVALID_PARAMS blocks.N.slots.subject`,而且整份产物一起被打回来。
                   ★组包边界(canvasBridge)同批也补了钳子:存量草稿里已经躺着 >120 的值,
                     只改这里救不回已经存下的稿子。 */
                maxLength={120}
              />
              {/* ★「让 AI 补细节」(2026-08-05,D3 第二腿):折叠起来只是**不摆在脸上**,
                  空着的槽仍然是空的 —— 走查实测「环境」「画面里还要有什么」两格从头到尾没人填,
                  喂给引擎的画面描述只剩主体 + 光影枚举,出来的图自然没场景没细节。
                  这个按钮把那句话交给 ai 拆成槽(只填空的,已填的一个字不覆盖)。 */}
              {inferSlotsMut && (
                <button
                  type="button"
                  title={
                    hasEmptySlot
                      ? "把这句话拆成画面参数,只填空着的格子"
                      : "画面参数已经填满了,没有空格子要补"
                  }
                  disabled={
                    inferSlotsMut.isPending ||
                    !String((p.slots as ImageSlots).subject ?? "").trim() ||
                    /* ★全填满就禁用(2026-08-05 走查):否则用户点下去、转一圈、只得到一句
                       「补不出更多细节」—— 那不是失败,是本来就没有空格子。按钮该在点之前
                       就说清楚这件事,而不是花一次 LLM 调用去告诉他。 */
                    !hasEmptySlot
                  }
                  onClick={() => inferSlotsMut.mutate()}
                  className="inline-flex items-center gap-1.5 self-start rounded-md border border-primary/40 bg-primary/5 px-2.5 py-1.5 text-2xs font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
                >
                  {inferSlotsMut.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                  )}
                  {inferSlotsMut.isPending ? "正在补细节…" : "让 AI 补画面细节"}
                </button>
              )}
              <details className="group rounded-md border border-border/60 bg-card/30 px-2.5 py-1.5">
                <summary className="cursor-pointer list-none text-2xs text-muted-foreground marker:hidden">
                  <span className="group-open:hidden">
                    {slotDigestLine(p.slots as ImageSlots) || "画面细节由 AI 补齐"} · 精调
                  </span>
                  <span className="hidden group-open:inline">收起精调</span>
                </summary>
                <div className="pt-2.5">
                  <SlotFields
                    slots={p.slots as ImageSlots}
                    hideSubject
                    onChange={(patch) => {
                      const next = { ...(p.slots as ImageSlots), ...patch };
                      // ★prompt 跟着槽重新派生 —— 它要显示给用户看,也是 blockDigest 的一部分。
                      //   不同步的话:用户改了槽,「图还是旧的」却说没改过(digest 只认 prompt 那会儿的坑)。
                      onPropsChange({ slots: next, prompt: slotSentence(next).join(";") });
                    }}
                  />
                </div>
              </details>
            </>
          ) : (
            <>
              <RefinableField
                label="这块要放什么画面"
                value={String(p.prompt ?? "")}
                onChange={(v) => onPropsChange({ prompt: v })}
                rawBlob={rawBlob}
                blockLabel={block.name}
                multiline
                maxLength={1200}
              />
              {/**
               * ★这栏只描述**画面**,写进来的字会被画出来(2026-08-14 四天反馈校对 TOP#3)。
               *
               * 琳琳 0810:「这个地方问我要放什么画面,我写了提示词,然后他作图的时候直接
               * 带出来了」——成品正文位置原样画着「封面下方白色底色区块,左侧配小图标,
               * 正文内容:…」(图 0810_4/15-17)。
               *
               * ★取证排掉了两条**看起来很像**的路(都不是成因):
               *   · 大框那条:真跑 comprehend,B 类原文判 rawTextKind=directive、整段进 intent,
               *     但 product.sellingPoints 干净(「专注企业通信服务十年」),没抄进版式描述;
               *   · plan 那条:真跑 plan-v2,产出的只是骨架(blocks[].props 全空),
               *     版式指令一个词都没漏进文案字段。
               * 剩下的就是这一栏:整段 prompt 直送文生图,模型看到「正文内容:…」这类指示
               * 就把它当成要画的字画上去。字段名「要放什么画面」又让人以为可以写排版要求。
               *
               * ★这是**止血不是根治**:根治要在出图前处理 prompt(或把排版指令识别出来),
               *   那要动 ai 侧且得真出图验。这里先把「写进来会被画出来」说在用户动手之前。
               */}
              <p className="-mt-1 text-2xs leading-relaxed text-muted-foreground">
                只写<b>画面</b>
                本身(拍什么、什么风格、什么氛围)。排版要求(几栏、底色、图标位置)写在这里没用 ——
                出图模型会把这段话<b>当成画面的一部分画出来</b>。要在图上出现的字,用下面那栏。
              </p>
            </>
          )}
          <RefinableField
            label="画面上要写的字(可空)"
            value={String(p.overlayText ?? "")}
            onChange={(v) => onPropsChange({ overlayText: v || undefined })}
            rawBlob={rawBlob}
            blockLabel={block.name}
            maxLength={40}
            // 同卡第二枚 AI 钮收成图标 —— 两枚同款文字钮摞着会被读成「重复」
            compact
          />
          {/* ★卖点上图的编辑口(2026-08-05):`overlayLines` 以前**整个 UI 层零引用** ——
              规划会产出、引擎会画进图里,而用户在界面上看不见也改不了,能力等于不存在。
              这里给它一个口子,预览那侧同批把它画出来,一改一看形成闭环。
              口径与 ai `ImageProps.overlayLines` 同源:≤3 条、每条 ≤14 字,超出的丢行不丢块。 */}
          {/* ★key 钉在块 id 上:换一块必须重挂,否则上一块的原文会留在输入框里
              (原文只活在编辑器本地,props 里存的是规范化结果,回不出空行/超长行)。 */}
          <OverlayLinesEditor
            key={`${block.id}-overlay`}
            value={Array.isArray(p.overlayLines) ? (p.overlayLines as string[]) : []}
            onChange={(lines) => onPropsChange({ overlayLines: lines })}
          />
        </>
      )}

      {family === "text" && (
        <>
          <RefinableField
            label="这块的文字"
            value={String(p.text ?? "")}
            onChange={(v) => onPropsChange({ text: v })}
            rawBlob={rawBlob}
            blockLabel={block.name}
            multiline
          />
          {/**
           * ★这栏的字会**原样**排进成品(0816 谢宇,单 5153769391104):他把一份逐块设计稿
           * 粘进来,末行的「呈现方式:大字痛点问句+短句解决方案,左右或上下对比排版」被
           * 当正文印上了图 —— 库实查载荷坐实是 FREEFORM_RAW 手动粘贴,不是规划漏字段。
           * 与图块「画面描述」那课(03a2d8c8,TOP#3)同款:代价要说在用户动手之前。
           * 不做静默过滤:用户的字不许我们删(逐字纪律);嗅探+确认弹窗归产品拍板(入产品待定)。
           */}
          <p
            data-testid="text-verbatim-hint"
            className="-mt-1 text-2xs leading-relaxed text-muted-foreground"
          >
            这里的字会<b>逐字排进成品</b> —— 从设计稿/文档粘贴时,「呈现方式:」「排版:」「配文:」这类
            <b>说明行</b>也会被当正文印上,出图前请先删除。
          </p>
          <div>
            <FieldLabel>副文本(可空)</FieldLabel>
            <Input
              value={String(p.sub ?? "")}
              maxLength={200}
              onChange={(e) => onPropsChange({ sub: e.target.value || undefined })}
              className="h-8 text-xs"
            />
          </div>
          {block.styleTag === "cite" && (
            <div>
              <FieldLabel>出处</FieldLabel>
              <Input
                value={String(p.source ?? "")}
                maxLength={60}
                onChange={(e) => onPropsChange({ source: e.target.value || undefined })}
                className="h-8 text-xs"
              />
            </div>
          )}
        </>
      )}

      {family === "list" && (
        <div>
          <FieldLabel>列表项 · 一行一条(回车换行加一条)</FieldLabel>
          <ListItemsEditor
            key={block.id}
            items={Array.isArray(p.items) ? (p.items as string[]) : []}
            onChange={(items) => onPropsChange({ items })}
          />
        </div>
      )}

      {family === "pairs" && (
        <>
          <div>
            <FieldLabel>标题(可空)</FieldLabel>
            <Input
              value={String(p.title ?? "")}
              maxLength={40}
              onChange={(e) => onPropsChange({ title: e.target.value || undefined })}
              className="h-8 text-xs"
            />
          </div>
          <div>
            <FieldLabel>
              {(isSourceCard(block.styleTag, block.name) && PAIRS_FIELD_LABEL[block.styleTag]) ||
                "条目 · 真数据手填,AI 不编"}
            </FieldLabel>
            {isSourceCard(block.styleTag, block.name) && PAIRS_HINT[block.styleTag] && (
              <p
                data-testid={`pairs-hint-${block.styleTag}`}
                className="mb-1.5 text-2xs leading-relaxed text-muted-foreground"
              >
                {PAIRS_HINT[block.styleTag]}
              </p>
            )}
            <PairsEditor
              items={
                Array.isArray(p.items) ? (p.items as Array<{ label: string; value: string }>) : []
              }
              onChange={(items) => onPropsChange({ items })}
              labelPh={
                isSourceCard(block.styleTag, block.name)
                  ? SOURCE_PLACEHOLDER[0]
                  : (PAIRS_PLACEHOLDER[block.styleTag]?.[0] ?? "名称")
              }
              valuePh={
                isSourceCard(block.styleTag, block.name)
                  ? SOURCE_PLACEHOLDER[1]
                  : (PAIRS_PLACEHOLDER[block.styleTag]?.[1] ?? "内容")
              }
            />
          </div>
        </>
      )}

      {family === "stat" && (
        <>
          <div>
            <FieldLabel>标题(可空)</FieldLabel>
            <Input
              value={String(p.title ?? "")}
              maxLength={40}
              onChange={(e) => onPropsChange({ title: e.target.value || undefined })}
              className="h-8 text-xs"
            />
          </div>
          <div>
            <FieldLabel>大数字 · 真数据手填</FieldLabel>
            <PairsEditor
              items={(Array.isArray(p.items)
                ? (p.items as Array<{ value: string; label: string }>)
                : []
              ).map((it) => ({ label: it.value, value: it.label }))}
              onChange={(items) =>
                onPropsChange({
                  items: items.map((it) => ({ value: it.label.slice(0, 20), label: it.value })),
                })
              }
              labelPh="92.6%"
              valuePh="指标名"
            />
          </div>
        </>
      )}

      {family === "chart" && (
        <>
          <div className="flex gap-3">
            <div className="flex-1">
              <FieldLabel>标题(可空)</FieldLabel>
              <Input
                value={String(p.title ?? "")}
                maxLength={40}
                onChange={(e) => onPropsChange({ title: e.target.value || undefined })}
                className="h-8 text-xs"
              />
            </div>
            <div className="w-20">
              <FieldLabel>单位</FieldLabel>
              <Input
                value={String(p.unit ?? "")}
                maxLength={12}
                onChange={(e) => onPropsChange({ unit: e.target.value || undefined })}
                className="h-8 text-xs"
              />
            </div>
          </div>
          <div>
            <FieldLabel>数据序列 · 坐标/数值取真数据渲染</FieldLabel>
            {/* ★7.29 反馈任务 8:这里以前是 `value: Number(it.value) || 0` —— 填文字不报错、
                直接变成 0,用户以为自己填了东西。现在:①用户敲的字**原样留在输入框**(存进
                rawSeries,不被改写)②非数字**不落 0**,那一行标红并说清该用哪个块
                ③给一键切到「前后对比」的按钮 —— 用户真正想做的是文字对照,那种块本来就有。 */}
            <PairsEditor
              items={seriesRows}
              onChange={(items) => {
                setRawSeries(items.map((x) => x.value));
                onPropsChange({
                  series: items.map((it, i) => {
                    const parsed = parseSeriesValue(it.value);
                    const prev = (Array.isArray(p.series) ? p.series : [])[i] as
                      | { value?: number }
                      | undefined;
                    return {
                      label: it.label.slice(0, 30),
                      // 解析失败保留上一次的合法值(**不写 0**);还没填过就留 null,
                      // 由出图前的校验拦住,不冒充「用户填了 0」。
                      value: parsed.ok ? parsed.value : (prev?.value ?? null),
                      highlight: i === 0 && block.styleTag === "comparebar",
                    };
                  }),
                });
              }}
              labelPh="本品"
              valuePh="数值"
              numericErrors={seriesErrors}
            />
            {Object.keys(seriesErrors).length > 0 && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-2 h-7 text-xs"
                onClick={() => onSwitchStyle?.(TEXT_PAIR_FALLBACK_TAG)}
                disabled={!onSwitchStyle}
                title={
                  onSwitchStyle
                    ? undefined
                    : "这个入口暂不支持换块,请手动删掉本块再加一个「前后对比」"
                }
              >
                换成「{STYLE_META[TEXT_PAIR_FALLBACK_TAG]?.label ?? "前后对比"}」块(能填文字)
              </Button>
            )}
          </div>
        </>
      )}

      {family === "table" && (
        <>
          <div>
            <FieldLabel>表头 · 逗号分隔</FieldLabel>
            <Input
              value={(Array.isArray(p.headers) ? (p.headers as string[]) : []).join(",")}
              onChange={(e) => {
                const headers = e.target.value
                  .split(/[,,]/)
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .slice(0, 6);
                // 改表头必须顺手对齐已有行:此前只更新 headers,行列一错配,之后每次
                // 预览/出图都吃 ai 422「表格每行列数必须与表头一致」(2026-08-12)
                onPropsChange({ headers, rows: refitTableRows(p.rows, headers.length) });
              }}
              className="h-8 text-xs"
            />
          </div>
          <div>
            {/* P-B.1:Excel 直传填充(xlsx 懒加载单独 chunk,仅在真选文件时下载;首行=表头)。
                真数据红线不变:只搬用户表格数据,AI 不编数字。 */}
            <div className="mb-1.5 flex items-center gap-2">
              <input
                ref={xlsxInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                data-testid="longimage-xlsx-input"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  // codex 修:大文件在主线程解析会卡 UI;本 UI 上限 6列20行,2MB 足够
                  if (file.size > 2 * 1024 * 1024) {
                    toast({
                      variant: "destructive",
                      title: "文件过大",
                      description: "Excel 请控制在 2MB 内(本表格块最多取 6 列 20 行)",
                    });
                    return;
                  }
                  void (async () => {
                    try {
                      const XLSX = await import("xlsx");
                      // sheetRows:只解析前 40 行(输入侧截断,不靠事后 slice)
                      const wb = XLSX.read(await file.arrayBuffer(), {
                        type: "array",
                        sheetRows: 40,
                      });
                      const sheet = wb.Sheets[wb.SheetNames[0]];
                      const grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
                        header: 1,
                        raw: false,
                      }) as unknown[][];
                      const clean = grid
                        .map((r) =>
                          (Array.isArray(r) ? r : []).map((c) =>
                            String(c ?? "")
                              .trim()
                              .slice(0, 60)
                          )
                        )
                        .filter((r) => r.some(Boolean));
                      if (clean.length < 2) {
                        toast({
                          variant: "destructive",
                          title: "Excel 内容不足",
                          description: "至少需要表头一行 + 数据一行(读取首个工作表)",
                        });
                        return;
                      }
                      const headers = clean[0].slice(0, 6);
                      const rows = clean.slice(1, 21).map((r) => {
                        const fixed = r.slice(0, headers.length);
                        while (fixed.length < headers.length) fixed.push("");
                        return fixed;
                      });
                      onPropsChange({ headers, rows });
                      toast({
                        title: "已从 Excel 填充",
                        description: `表头 ${headers.length} 列 · 数据 ${rows.length} 行(首个工作表)`,
                      });
                    } catch (err) {
                      toast({
                        variant: "destructive",
                        title: "Excel 解析失败",
                        description: errorToast(err).description,
                      });
                    }
                  })();
                }}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 gap-1"
                onClick={() => xlsxInputRef.current?.click()}
              >
                <Upload className="h-3 w-3" />
                上传 Excel 填充
              </Button>
              <span className="text-2xs text-muted-foreground">首行=表头 · ≤6列20行</span>
            </div>
            <FieldLabel>行 · 一行一条,列用 | 分隔(粘贴 Excel 行即可)</FieldLabel>
            <Textarea
              value={(Array.isArray(p.rows) ? (p.rows as string[][]) : [])
                .map((r) => r.join(" | "))
                .join("\n")}
              rows={4}
              onChange={(e) => {
                const headers = Array.isArray(p.headers) ? (p.headers as string[]) : [];
                const rows = e.target.value
                  .split("\n")
                  .map((line) => line.split(/\||\t/).map((c) => c.trim().slice(0, 60)))
                  .filter((r) => r.some(Boolean))
                  .map((r) => {
                    const fixed = r.slice(0, headers.length);
                    while (fixed.length < headers.length) fixed.push("");
                    return fixed;
                  })
                  .slice(0, 20);
                onPropsChange({ rows });
              }}
              className="font-mono text-xs"
            />
          </div>
        </>
      )}

      {family === "rating" && (
        <>
          <div>
            <FieldLabel>分制</FieldLabel>
            <Seg
              options={[
                { v: "5", label: "五星" },
                { v: "10", label: "十分制" },
              ]}
              value={String(p.scale ?? 5)}
              onChange={(v) => onPropsChange({ scale: Number(v) })}
            />
          </div>
          <div>
            <FieldLabel>评分维度</FieldLabel>
            <PairsEditor
              items={(Array.isArray(p.items)
                ? (p.items as Array<{ label: string; score: number }>)
                : []
              ).map((it) => ({ label: it.label, value: String(it.score) }))}
              onChange={(items) =>
                onPropsChange({
                  items: items.map((it) => ({
                    label: it.label.slice(0, 30),
                    score: Math.max(0, Math.min(Number(p.scale ?? 5), Number(it.value) || 0)),
                  })),
                })
              }
              labelPh="出汁率"
              valuePh="4.8"
            />
          </div>
        </>
      )}

      {family === "qr" && (
        <>
          <div>
            <FieldLabel>二维码内容</FieldLabel>
            <Input
              value={String(p.content ?? "")}
              maxLength={512}
              onChange={(e) => onPropsChange({ content: e.target.value })}
              className="h-8 text-xs"
            />
          </div>
          <div>
            <FieldLabel>引导文案</FieldLabel>
            <Input
              value={String(p.caption ?? "")}
              maxLength={60}
              onChange={(e) => onPropsChange({ caption: e.target.value || undefined })}
              className="h-8 text-xs"
            />
          </div>
        </>
      )}

      {meta.primitive === "data" && (
        <p
          data-testid="data-verbatim-hint"
          className="rounded-md bg-primary/5 px-2.5 py-2 text-2xs text-muted-foreground"
        >
          真数据可视化:数字/文案<b>逐字渲染进图</b>,AI 不编数字。从设计稿粘贴时,
          「呈现方式」「排版」这类说明行也会被当成一条数据印上,出图前请先删除。
        </p>
      )}
      <details className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 [&[open]>summary]:mb-2">
        <summary className="cursor-pointer select-none text-2xs font-semibold text-muted-foreground">
          版面参数 · 比例 / 对齐 / 背景 / 名称
        </summary>
        <div className="space-y-3">{paramFields}</div>
      </details>
    </div>
  );
}
